import type { Group, Role, User } from '@gcv/shared';
import { IANATimezones, MainFeatures, OrganizationRoleResolver, TaskCategory } from '@gcv/shared';
import { inject, injectable } from 'inversify';
import { action, makeAutoObservable } from 'mobx';

import { BanksApi, UsersApi } from 'api';
import { FiBankStore } from 'stores/FiBankStore';
import { FiDispensaryStore } from 'stores/FiDispensaryStore';
import type { SelectOption } from 'ui';
import { orgFeatureEnabled } from 'util/org.util';
import type { TaskAssignee } from './tasks-modal.model';
import { TaskAssigneeType } from './tasks-modal.model';

export interface GetAssigneesParams {
  /** For external (CRB) tasks, the dispensary org ID */
  orgId?: string;
  /** TaskCategory.SYSTEM or TaskCategory.USER */
  taskCategory?: TaskCategory;
  /** The original group IDs from the task (original_assigned_group_ids) */
  originalGroupIds?: string[];
  /** The original user IDs from the task (original_assigned_user_ids) */
  originalUserIds?: string[];
  /** Whether this is an external (CRB-facing) task */
  isExternal?: boolean;
  /** Whether this is a comment-type task (COMMENTS, COMMENTS_REPLY, COMMENT_MENTION) */
  isCommentTask?: boolean;
}

export interface PM {
  assignees: TaskAssignee[];
  accounts: SelectOption[];
  dispensaries: SelectOption[];
  timezone: IANATimezones;
  isFincenFeatureEnabled: boolean;
}

@injectable()
export class FiTasksModalRepo {
  @inject(FiBankStore) private fiBankStore: FiBankStore;
  @inject(FiDispensaryStore) private fiDispensaryStore: FiDispensaryStore;
  @inject(OrganizationRoleResolver) private roleResolver: OrganizationRoleResolver;

  @inject(UsersApi)
  private usersApi: UsersApi;
  @inject(BanksApi)
  private banksApi: BanksApi;

  constructor() {
    makeAutoObservable(this);
  }

  createTaskAssignees = async (staff: User[], groups: Group[]): Promise<TaskAssignee[]> => {
    const unassignableRoles: Role[] = ['bank_examiner'];
    const userAssignees = staff
      .filter(
        (s) =>
          s.invitation_status === 'accepted' &&
          !unassignableRoles.some((role) => this.roleResolver.userHasRole(groups, s, role))
      )
      .map((u) => {
        const fullName = `${u.firstName} ${u.lastName}`;

        return {
          id: u.id,
          name: fullName,
          userIds: [u.id],
          type: TaskAssigneeType.USER
        } as TaskAssignee;
      });

    const groupAssignees = groups
      .filter((g) => !unassignableRoles.some((role) => g.roles.includes(role)))
      .filter((g) => g.users.length > 0)
      .map((g) => {
        return {
          id: g.id,
          name: g.name,
          userIds: [g.id],
          type: TaskAssigneeType.GROUP
        } as TaskAssignee;
      });

    return [...userAssignees, ...groupAssignees];
  };

  public programmersModel: PM = {
    assignees: [],
    accounts: [],
    dispensaries: [],
    timezone: IANATimezones.America_NewYork,
    isFincenFeatureEnabled: false
  };

  updateProgrammersModel = action((programmersModel: Partial<PM>) => {
    this.programmersModel = { ...this.programmersModel, ...programmersModel };
  });

  load = action(async () => {
    const timezone = this.fiBankStore.bank.iana_timezone;
    const isFincenFeatureEnabled =
      orgFeatureEnabled(this.fiBankStore.bank, MainFeatures.FinCEN, 'CTR') ||
      orgFeatureEnabled(this.fiBankStore.bank, MainFeatures.FinCEN, 'SAR');

    const accounts = this.fiDispensaryStore
      .allOrgs()
      .sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1))
      .map((org) => {
        return {
          value: org.id,
          label: org.name
        } as SelectOption;
      });

    const dispensaries = this.fiDispensaryStore.dispensaries
      .sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1))
      .map((org) => {
        return {
          value: org.id,
          label: org.name
        } as SelectOption;
      });

    const assignees = await this.createTaskAssignees(this.fiBankStore.staff, this.fiBankStore.bank.groups);

    this.updateProgrammersModel({
      timezone,
      assignees,
      accounts,
      dispensaries,
      isFincenFeatureEnabled
    });
  });

  getAssignees = async (params: GetAssigneesParams = {}): Promise<TaskAssignee[]> => {
    const {
      orgId = '',
      taskCategory,
      originalGroupIds = [],
      originalUserIds = [],
      isExternal = false,
      isCommentTask = false
    } = params;

    // Comment tasks cannot be reassigned — return empty
    if (isCommentTask) {
      return [];
    }

    let assignees: TaskAssignee[];
    let groups: Group[];

    if (orgId !== '') {
      const [dispUsers, dispensaryDetails] = await Promise.all([
        this.usersApi.getDispensaryUsers(this.fiBankStore.bank.id, orgId),
        this.banksApi.getBankDispensary(this.fiBankStore.bank.id, orgId)
      ]);
      assignees = await this.createTaskAssignees(dispUsers as unknown as User[], dispensaryDetails.groups);
      groups = dispensaryDetails.groups;
    } else {
      assignees = await this.createTaskAssignees(this.fiBankStore.staff, this.fiBankStore.bank.groups);
      groups = this.fiBankStore.bank.groups;
    }

    // User-created (manual) tasks: any user/group in the org (no further filtering)
    if (taskCategory === TaskCategory.USER) {
      return assignees;
    }

    // System tasks: filter based on original assignment
    if (originalGroupIds.length > 0) {
      // Original assignee was group(s) — restrict to those groups + users within them
      const originalGroupIdSet = new Set(originalGroupIds);
      const originalGroups = groups.filter((g) => originalGroupIdSet.has(g.id));
      const originalUserIdSet = new Set(originalGroups.flatMap((g) => g.users));

      assignees = assignees.filter((a) => {
        if (a.type === TaskAssigneeType.GROUP) {
          return originalGroupIdSet.has(a.id);
        }
        return originalUserIdSet.has(a.id);
      });
    } else if (!isExternal && originalUserIds.length > 0) {
      // Bank-facing system task with original single user assignment
      // Allow: original user + bank_admin/bank_account_owner groups + users in those groups
      const adminAoRoles: Set<Role> = new Set(['bank_admin', 'bank_primary_contact']);
      const adminAoGroups = groups.filter((g) => g.roles.some((r) => adminAoRoles.has(r)));
      const allowedUserIds = new Set([...originalUserIds, ...adminAoGroups.flatMap((g) => g.users)]);
      const allowedGroupIds = new Set(adminAoGroups.map((g) => g.id));

      assignees = assignees.filter((a) => {
        if (a.type === TaskAssigneeType.GROUP) {
          return allowedGroupIds.has(a.id);
        }
        return allowedUserIds.has(a.id);
      });
    }

    return assignees;
  };
}
